// Prohibited Words Moderation API
export const prohibitedWordApi = {
  getWords: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/prohibited-words${query ? `?${query}` : ''}`);
  },
  addWord: (body) => apiRequest('/admin/prohibited-words', { method: 'POST', body }),
  updateWord: (id, body) => apiRequest(`/admin/prohibited-words/${id}`, { method: 'PUT', body }),
  deleteWord: (id) => apiRequest(`/admin/prohibited-words/${id}`, { method: 'DELETE' }),
  verifyText: (text) => apiRequest('/community/verify-text', { method: 'POST', body: { text } }),
};

// Community Wall & Posts API
export const communityApi = {
  getPosts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/community/posts${query ? `?${query}` : ''}`);
  },
  createPost: (body) => apiRequest('/community/posts', { method: 'POST', body }),
  addComment: (postId, body) => apiRequest(`/community/posts/${postId}/comment`, { method: 'POST', body }),
  deleteComment: (postId, commentId) => apiRequest(`/community/posts/${postId}/comment/${commentId}`, { method: 'DELETE' }),
  likePost: (postId) => apiRequest(`/community/posts/${postId}/like`, { method: 'POST' }),
  deletePost: (postId) => apiRequest(`/community/posts/${postId}`, { method: 'DELETE' }),
  verifyText: (text) => apiRequest('/community/verify-text', { method: 'POST', body: { text } }),
};

// Therapist Chat API
export const chatApi = {
  getChats: () => apiRequest('/chats/me'),
  getMessages: (chatId) => apiRequest(`/chats/${chatId}/messages`),
  sendMessage: (recipientId, content) => apiRequest('/chats/send', { method: 'POST', body: { recipientId, content } }),
  markRead: (chatId) => apiRequest(`/chats/${chatId}/read`, { method: 'POST' }),
  getChatmitraStatus: () => apiRequest('/chats/chatmitra/status'),
};
// Reviews & Ratings API
export const reviewApi = {
  createReview: async (body) => {
    const bookingId = body?.bookingId || body?._id;
    if (bookingId) {
      try {
        return await apiRequest(`/bookings/${bookingId}/review`, { method: 'POST', body });
      } catch (err) {
        if (!err.message || !err.message.includes('Route not found')) {
          throw err;
        }
      }
    }
    try {
      return await apiRequest('/bookings/reviews', { method: 'POST', body });
    } catch (err) {
      if (!err.message || !err.message.includes('Route not found')) {
        throw err;
      }
    }
    return await apiRequest('/reviews', { method: 'POST', body });
  },
  getTherapistReviews: (profileId) => apiRequest(`/reviews/therapist/${profileId}`),
};
// Pro Session Bundles API
export const bundleApi = {
  createBundle: (body) => apiRequest('/bundles', { method: 'POST', body }),
  getTherapistBundles: (profileId) => apiRequest(`/bundles/therapist/${profileId}`),
  purchaseBundle: (bundleId) => apiRequest('/bundles/purchase', { method: 'POST', body: { bundleId } }),
  getMyCredits: () => apiRequest('/bundles/my-credits'),
};
// Therapist Events API
export const eventsApi = {
  getMyEvents: () => apiRequest('/events/me'),
  getEligibility: () => apiRequest('/events/me/eligibility'),
  getPublishPlans: () => apiRequest('/events/publish-plans'),
  addEvent: (body) => apiRequest('/events', { method: 'POST', body }),
  updateEvent: (eventId, body) => apiRequest(`/events/me/${eventId}`, { method: 'PUT', body }),
  deleteEvent: (eventId) => apiRequest(`/events/me/${eventId}`, { method: 'DELETE' }),
  extendEvent: (eventId, body) => apiRequest(`/events/me/${eventId}/extend`, { method: 'POST', body }),
  republishEvent: (eventId, body) => apiRequest(`/events/me/${eventId}/republish`, { method: 'POST', body }),
  createPublishOrder: (body) => apiRequest('/events/me/create-publish-order', { method: 'POST', body }),
  uploadEventVideo: (formData) => fileUploadRequest('/upload/video', formData),
};
// Therapist Notes & Collaborative Worksheets API
export const clientNoteApi = {
  getNotes: () => apiRequest('/client-notes/me'),
  addNote: (body) => apiRequest('/client-notes/me', { method: 'POST', body }),
  updateNote: (noteId, body) => apiRequest(`/client-notes/me/${noteId}`, { method: 'PUT', body }),
  deleteNote: (noteId) => apiRequest(`/client-notes/me/${noteId}`, { method: 'DELETE' }),
  getClientWorksheets: async () => {
    try {
      return await apiRequest('/client-notes/my-worksheets');
    } catch (err) {
      return await apiRequest('/worksheets/my-worksheets');
    }
  },
  getWorksheetNoteById: async (noteId) => {
    try {
      return await apiRequest(`/client-notes/worksheets/${noteId}`);
    } catch (err1) {
      try {
        return await apiRequest(`/worksheets/note/${noteId}`);
      } catch (err2) {
        try {
          return await apiRequest(`/worksheets/${noteId}/form`);
        } catch (err3) {
          return await apiRequest(`/worksheets/${noteId}`);
        }
      }
    }
  },
  syncLiveWorksheet: async (noteId, body) => {
    try {
      return await apiRequest(`/client-notes/worksheets/${noteId}/sync`, { method: 'PUT', body });
    } catch (err) {
      return await apiRequest(`/worksheets/${noteId}/sync`, { method: 'PUT', body });
    }
  },
  submitWorksheetForm: async (noteId, body) => {
    try {
      return await apiRequest(`/client-notes/worksheets/${noteId}/submit-form`, { method: 'POST', body });
    } catch (err1) {
      try {
        return await apiRequest(`/worksheets/${noteId}/submit-form`, { method: 'POST', body });
      } catch (err2) {
        try {
          return await apiRequest(`/client-notes/${noteId}/submit-form`, { method: 'POST', body });
        } catch (err3) {
          try {
            return await apiRequest(`/worksheets/${noteId}/form-responses`, { method: 'POST', body });
          } catch (err4) {
            return await apiRequest(`/client-notes/worksheets/${noteId}/sync`, {
              method: 'PUT',
              body: {
                formResponses: body.formResponses,
                clientResponses: body.formResponses,
                isSubmitted: body.isSubmitted !== false,
                isFormSubmitted: body.isSubmitted !== false,
              },
            });
          }
        }
      }
    }
  },
  saveTherapistFeedback: async (noteId, body) => {
    try {
      return await apiRequest(`/client-notes/worksheets/${noteId}/feedback`, { method: 'PUT', body });
    } catch (err) {
      return await apiRequest(`/worksheets/${noteId}/feedback`, { method: 'PUT', body });
    }
  },
};
const getBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL !== '/api') {
    return process.env.REACT_APP_API_BASE_URL;
  }
  if (typeof window !== 'undefined') {
    // If running in local React development server on port 3000, connect to local backend port 5000
    if (window.location.hostname === 'localhost' && window.location.port === '3000') {
      return 'http://localhost:5000/api';
    }
    // In production or live server, use relative path or current origin
    return `${window.location.origin}/api`;
  }
  return '/api';
};

const API_BASE_URL = getBaseUrl();

const AUTH_STORAGE_KEY = 'mental17_auth';

function getStoredAuth() {
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw || raw === 'null' || raw === 'undefined') {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
}

export function saveAuthSession(session) {
  if (!session) {
    clearAuthSession();
    return;
  }
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  if (session?.token) {
    window.localStorage.setItem('authToken', session.token);
  }
  window.dispatchEvent(new Event('auth-changed'));
}

export function clearAuthSession() {
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
  window.localStorage.removeItem('authToken');
  try {
    window.sessionStorage.clear();
  } catch (e) {}
  window.dispatchEvent(new Event('auth-changed'));
}

export function getAuthSession() {
  return getStoredAuth();
}

async function apiRequest(path, options = {}) {
  const session = getStoredAuth();
  const token = options.token || session?.token;

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : null;

  if (!response.ok) {
    // If backend returned detailed validation errors, include them in the thrown message
    let message = payload?.message || 'Request failed';
    if (payload?.errors && Array.isArray(payload.errors) && payload.errors.length > 0) {
      const details = payload.errors.map((e) => `${e.path || e.param || 'field'}: ${e.msg}`).join('; ');
      message = `${message} - ${details}`;
    }
    const err = new Error(message);
    if (payload && typeof payload === 'object') {
      Object.assign(err, payload);
    }
    throw err;
  }

  return payload;
}

async function fileUploadRequest(path, formData, options = {}) {
  const session = getStoredAuth();
  const token = options.token || session?.token;

  const headers = {
    ...(options.headers || {}),
  };

  // Don't set Content-Type for FormData - browser will handle it
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || 'POST',
    headers,
    body: formData,
  });

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : null;

  if (!response.ok) {
    const message = payload?.message || 'Request failed';
    throw new Error(message);
  }

  return payload;
}

export const authApi = {
  login: (body) => apiRequest('/auth/login', { method: 'POST', body }),
  register: (body) => apiRequest('/auth/register', { method: 'POST', body }),
  verifyEmail: (body) => apiRequest('/auth/verify-email', { method: 'POST', body }),
  forgotPassword: (body) => apiRequest('/auth/forgot-password', { method: 'POST', body }),
  resetPassword: (body) => apiRequest('/auth/reset-password', { method: 'POST', body }),
  me: () => apiRequest('/auth/me'),
};

export const physioApi = {
  me: () => apiRequest('/physios/me'),
  updateMyProfile: (body) => apiRequest('/physios/me/profile', { method: 'PUT', body }),
  addDocument: (body, options = {}) => apiRequest('/physios/me/documents', { method: 'POST', body, ...options }),
  addVerificationDocument: (body, options = {}) => apiRequest('/physios/me/documents', { method: 'POST', body, ...options }),
  search: (query = '') => apiRequest(`/physios/search${query ? `?${query}` : ''}`),

  // Blogs CRUD for therapist dashboard
  getBlogs: () => apiRequest('/physios/me/blogs'),
  addBlog: (body) => apiRequest('/physios/me/blogs', { method: 'POST', body }),
  updateBlog: (blogId, body) => apiRequest(`/physios/me/blogs/${blogId}`, { method: 'PUT', body }),
  deleteBlog: (blogId) => apiRequest(`/physios/me/blogs/${blogId}`, { method: 'DELETE' }),
  uploadBlogImage: (formData) => fileUploadRequest('/upload/file', formData),
  uploadIntroVideo: (formData) => fileUploadRequest('/upload/video', formData),
  uploadVideo: (formData) => fileUploadRequest('/upload/video', formData),

  // Registration Payment
  createRegistrationOrder: (body = {}, options = {}) => apiRequest('/physios/me/registration-order', { method: 'POST', body, ...options }),
  verifyRegistrationPayment: (body, options = {}) => apiRequest('/physios/me/verify-registration-payment', { method: 'POST', body, ...options }),

  // Date & Time Slots / Availability
  getSlots: () => apiRequest('/physios/me/slots'),
  addSlot: (body) => apiRequest('/physios/me/slots', { method: 'POST', body }),
  deleteSlot: (slotId, slotObj) => {
    let url = `/physios/me/slots/${slotId || 'by-time'}`;
    if (slotObj?.date && slotObj?.startTime) {
      url += `?date=${encodeURIComponent(slotObj.date)}&startTime=${encodeURIComponent(slotObj.startTime)}`;
    }
    return apiRequest(url, { method: 'DELETE' });
  },
  clearAllSlots: () => apiRequest('/physios/me/slots/all/clear', { method: 'DELETE' }),
  updateWeeklySchedule: (body) => apiRequest('/physios/me/availability', { method: 'PUT', body }),
  getPublicSlots: (therapistId) => apiRequest(`/physios/${therapistId}/slots`),
};

export const bookingApi = {
  create: (body) => apiRequest('/bookings', { method: 'POST', body }),
  createOrder: (body) => apiRequest('/bookings/create-order', { method: 'POST', body }),
  verifyPayment: (body) => apiRequest('/bookings/verify-payment', { method: 'POST', body }),
  directBook: (body) => apiRequest('/bookings/direct-book', { method: 'POST', body }),
  mine: () => apiRequest('/bookings/me'),
  updateStatus: (bookingId, body) => apiRequest(`/bookings/${bookingId}/status`, { method: 'PATCH', body }),
  reschedule: (bookingId, body) => apiRequest(`/bookings/${bookingId}/reschedule`, { method: 'PATCH', body }),
  cancel: (bookingId, body) => apiRequest(`/bookings/${bookingId}/cancel`, { method: 'PATCH', body }),
  getBookingById: (bookingId) => apiRequest(`/bookings/${bookingId}`),
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/bookings${queryString ? `?${queryString}` : ''}`);
  },
};

// ✅ Therapist Session API (NEW)
export const sessionApi = {
  // Public endpoints - Browse sessions
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/sessions${queryString ? `?${queryString}` : ''}`);
  },
  getById: (sessionId) => apiRequest(`/sessions/${sessionId}`),

  // Therapist endpoints - Create & manage sessions
  createSession: (body) => apiRequest('/sessions', { method: 'POST', body }),
  getMySessionstherapist: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/sessions/therapist/my-sessions${queryString ? `?${queryString}` : ''}`);
  },
  updateSession: (sessionId, body) => apiRequest(`/sessions/${sessionId}`, { method: 'PUT', body }),
  cancelSession: (sessionId, body) => apiRequest(`/sessions/${sessionId}/cancel`, { method: 'POST', body }),
  getSessionAttendees: (sessionId) => apiRequest(`/sessions/${sessionId}/attendees`),

  // Client endpoints - Book sessions
  bookSession: (sessionId) => apiRequest(`/sessions/${sessionId}/book`, { method: 'POST' }),
  cancelBooking: (sessionId) => apiRequest(`/sessions/${sessionId}/cancel-booking`, { method: 'POST' }),
  getMyBookings: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/sessions/me/bookings${queryString ? `?${queryString}` : ''}`);
  },

  // Payment endpoints - Session payment
  createRazorpayOrder: (body) => apiRequest('/sessions/razorpay/order', { method: 'POST', body }),
  verifyRazorpayPayment: (body) => apiRequest('/sessions/razorpay/verify', { method: 'POST', body }),
  getBookingDetails: (bookingId) => apiRequest(`/sessions/booking/${bookingId}/details`),
  getBookingReceipt: (bookingId) => apiRequest(`/sessions/booking/${bookingId}/receipt`),
};

export const clientApi = {
  getProfile: () => apiRequest('/auth/me'),
  updateProfile: (body) => apiRequest('/auth/me', { method: 'PATCH', body }),
  changePassword: (body) => apiRequest('/auth/change-password', { method: 'PATCH', body }),
  getBookings: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/bookings/me${queryString ? `?${queryString}` : ''}`);
  },
  searchTherapists: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/physios/search${queryString ? `?${queryString}` : ''}`);
  },
  getBlogs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/blogs${queryString ? `?${queryString}` : ''}`);
  },
  getBlogById: (blogId) => apiRequest(`/blogs/${blogId}`),
};

export const publicApi = {
  getBlogs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/blogs${queryString ? `?${queryString}` : ''}`);
  },
  getBlogById: (blogId) => apiRequest(`/blogs/${blogId}`),
  getTestimonials: () => apiRequest('/testimonials'),
  getEvents: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/events${queryString ? `?${queryString}` : ''}`);
  },
};

export const testimonialApi = {
  getTestimonials: () => apiRequest('/testimonials'),
  adminGetTestimonials: () => apiRequest('/admin/testimonials'),
  adminCreateTestimonial: (body) => apiRequest('/admin/testimonials', { method: 'POST', body }),
  adminUpdateTestimonial: (id, body) => apiRequest(`/admin/testimonials/${id}`, { method: 'PUT', body }),
  adminDeleteTestimonial: (id) => apiRequest(`/admin/testimonials/${id}`, { method: 'DELETE' }),
};

export const blogApi = publicApi;

export const therapistApi = {
  getProfile: () => apiRequest('/physios/me'),
  updateProfile: (body) => apiRequest('/physios/me/profile', { method: 'PUT', body }),
  getMyBookings: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/bookings/me${queryString ? `?${queryString}` : ''}`);
  },
  addAvailability: (body) => apiRequest('/physios/me/availability', { method: 'POST', body }),
};

export const notificationApi = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/notifications${queryString ? `?${queryString}` : ''}`);
  },
  getUnreadCount: () => apiRequest('/notifications/unread/count'),
  markAsRead: (notificationId) => apiRequest(`/notifications/${notificationId}/read`, { method: 'PATCH' }),
  markAllAsRead: () => apiRequest('/notifications/mark-all/read', { method: 'PATCH' }),
  delete: (notificationId) => apiRequest(`/notifications/${notificationId}`, { method: 'DELETE' }),
  deleteAll: () => apiRequest('/notifications/delete-all/all', { method: 'DELETE' }),
};

export const adminApi = {
  // ==================== USER MANAGEMENT ====================
  getUsers: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/users${queryString ? `?${queryString}` : ''}`);
  },
  getUserById: (userId) => apiRequest(`/admin/users/${userId}`),
  createUser: (body) => apiRequest('/admin/users', { method: 'POST', body }),
  updateUser: (userId, body) => apiRequest(`/admin/users/${userId}`, { method: 'PATCH', body }),
  deleteUser: (userId) => apiRequest(`/admin/users/${userId}`, { method: 'DELETE' }),

  // ==================== THERAPIST MANAGEMENT ====================
  getTherapists: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/therapists${queryString ? `?${queryString}` : ''}`);
  },
  getTherapistById: (therapistId) => apiRequest(`/admin/therapists/${therapistId}`),
  verifyTherapist: (therapistId, body) => apiRequest(`/admin/therapists/${therapistId}/verify`, { method: 'PATCH', body }),
  updateTherapistMembership: (therapistId, body) => apiRequest(`/admin/therapists/${therapistId}/membership`, { method: 'PATCH', body }),
  updateTherapistSessions: (therapistId, completedSessions) =>
    apiRequest(`/admin/badges/admin/therapists/${therapistId}/sessions`, {
      method: 'PATCH',
      body: { completedSessions },
    }),

  // ==================== BOOKING MANAGEMENT ====================
  getBookings: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/bookings${queryString ? `?${queryString}` : ''}`);
  },
  getBookingById: (bookingId) => apiRequest(`/admin/bookings/${bookingId}`),
  updateBookingStatus: (bookingId, body) => apiRequest(`/admin/bookings/${bookingId}/status`, { method: 'PATCH', body }),
  cancelBooking: (bookingId) => apiRequest(`/admin/bookings/${bookingId}`, { method: 'DELETE' }),

  // ==================== BLOG MANAGEMENT ====================
  getBlogs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/blogs${queryString ? `?${queryString}` : ''}`);
  },
  createBlog: (body) => apiRequest('/blogs', { method: 'POST', body }),
  updateBlog: (blogId, body) => apiRequest(`/blogs/${blogId}`, { method: 'PATCH', body }),
  reviewBlog: (blogId, body) => apiRequest(`/admin/blogs/${blogId}/review`, { method: 'PATCH', body }),
  deleteBlog: (blogId) => apiRequest(`/admin/blogs/${blogId}`, { method: 'DELETE' }),
  uploadBlogImage: (formData) => fileUploadRequest('/upload/file', formData),

  // ==================== EVENT MANAGEMENT ====================
  getEvents: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/events${queryString ? `?${queryString}` : ''}`);
  },
  getEventById: (eventId) => apiRequest(`/admin/events/${eventId}`),
  createEvent: (body) => apiRequest('/admin/events', { method: 'POST', body }),
  updateEvent: (eventId, body) => apiRequest(`/admin/events/${eventId}`, { method: 'PUT', body }),
  reviewEvent: (eventId, body) => apiRequest(`/admin/events/${eventId}/review`, { method: 'PATCH', body }),
  deleteEvent: (eventId) => apiRequest(`/admin/events/${eventId}`, { method: 'DELETE' }),
  uploadEventVideo: (formData) => fileUploadRequest('/upload/video', formData),

  // ==================== PRODUCT MANAGEMENT ====================
  getProducts: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/products${queryString ? `?${queryString}` : ''}`);
  },
  reviewProduct: (productId, body) => apiRequest(`/admin/products/${productId}/review`, { method: 'PATCH', body }),
  deleteProduct: (productId) => apiRequest(`/admin/products/${productId}`, { method: 'DELETE' }),

  // ==================== EVENT PUBLISH WEEKS & PRICING PLANS ====================
  getEventPublishPlans: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    try {
      return await apiRequest(`/admin/event-publish-plans${queryString ? `?${queryString}` : ''}`);
    } catch (err) {
      return await apiRequest(`/event-publish-plans${queryString ? `?${queryString}` : ''}`);
    }
  },
  getEventPublishPlanById: async (id) => {
    try {
      return await apiRequest(`/admin/event-publish-plans/${id}`);
    } catch (err) {
      return await apiRequest(`/event-publish-plans/${id}`);
    }
  },
  createEventPublishPlan: async (body) => {
    try {
      return await apiRequest('/admin/event-publish-plans', { method: 'POST', body });
    } catch (err) {
      return await apiRequest('/event-publish-plans', { method: 'POST', body });
    }
  },
  updateEventPublishPlan: async (id, body) => {
    try {
      return await apiRequest(`/admin/event-publish-plans/${id}`, { method: 'PUT', body });
    } catch (err) {
      return await apiRequest(`/event-publish-plans/${id}`, { method: 'PUT', body });
    }
  },
  deleteEventPublishPlan: async (id) => {
    try {
      return await apiRequest(`/admin/event-publish-plans/${id}`, { method: 'DELETE' });
    } catch (err) {
      return await apiRequest(`/event-publish-plans/${id}`, { method: 'DELETE' });
    }
  },
  toggleEventPublishPlan: async (id) => {
    try {
      return await apiRequest(`/admin/event-publish-plans/${id}/toggle`, { method: 'PATCH' });
    } catch (err) {
      return await apiRequest(`/event-publish-plans/${id}/toggle`, { method: 'PATCH' });
    }
  },

  // ==================== ANALYTICS ====================
  getOverviewAnalytics: () => apiRequest('/admin/analytics/overview'),
  getRevenueAnalytics: () => apiRequest('/admin/analytics/revenue'),
  getCommissionAnalytics: () => apiRequest('/admin/analytics/commission'),
  getTopTherapists: () => apiRequest('/admin/analytics/top-physios'),

  // ==================== THERAPIST CATEGORIES ====================
  getTherapistCategories: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/therapist-categories${queryString ? `?${queryString}` : ''}`);
  },
  getTherapistCategoryById: (id) => apiRequest(`/admin/therapist-categories/${id}`),
  createTherapistCategory: (body) => apiRequest('/admin/therapist-categories', { method: 'POST', body }),
  updateTherapistCategory: async (id, body) => {
    try {
      return await apiRequest(`/admin/therapist-categories/${id}`, { method: 'PUT', body });
    } catch (err) {
      if (err.message && (err.message.includes('404') || err.message.includes('405') || err.message.includes('Cannot PUT'))) {
        return await apiRequest(`/admin/therapist-categories/${id}`, { method: 'PATCH', body });
      }
      throw err;
    }
  },
  deleteTherapistCategory: (id) => apiRequest(`/admin/therapist-categories/${id}`, { method: 'DELETE' }),
  toggleTherapistCategory: (id) => apiRequest(`/admin/therapist-categories/${id}/toggle`, { method: 'PATCH' }),
  toggleTherapistCategoryPrescription: (id) => apiRequest(`/admin/therapist-categories/${id}/toggle-prescription`, { method: 'PATCH' }),

  // ==================== SESSION TYPES CRUD (IN-PERSON, VIDEO, CALL) ====================
  getSessionTypes: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/session-types${queryString ? `?${queryString}` : ''}`);
  },
  getSessionTypeById: (id) => apiRequest(`/session-types/${id}`),
  createSessionType: (body) => apiRequest('/admin/session-types', { method: 'POST', body }),
  updateSessionType: (id, body) => apiRequest(`/admin/session-types/${id}`, { method: 'PUT', body }),
  deleteSessionType: (id) => apiRequest(`/admin/session-types/${id}`, { method: 'DELETE' }),

  // ==================== LEGACY ROUTES ====================
  pendingVerifications: () => apiRequest('/admin/verifications/pending'),
  reviewVerification: (profileId, body) => apiRequest(`/admin/verifications/${profileId}`, { method: 'PATCH', body }),
};

export const sessionTypeApi = {
  getPublicSessionTypes: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/session-types${queryString ? `?${queryString}` : ''}`);
  },
  getSessionTypeById: (id) => apiRequest(`/session-types/${id}`),
};

export const categoryApi = {
  getPublicCategories: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/therapist-categories${queryString ? `?${queryString}` : ''}`);
  },
};

export const blogCategoryApi = {
  getPublicCategories: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/blog-categories${queryString ? `?${queryString}` : ''}`);
  },
  getCategoryById: (id) => apiRequest(`/blog-categories/${id}`),
  adminGetCategories: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/blog-categories${queryString ? `?${queryString}` : ''}`);
  },
  adminCreateCategory: (body) => apiRequest('/admin/blog-categories', { method: 'POST', body }),
  adminUpdateCategory: (id, body) => apiRequest(`/admin/blog-categories/${id}`, { method: 'PUT', body }),
  adminDeleteCategory: (id) => apiRequest(`/admin/blog-categories/${id}`, { method: 'DELETE' }),
  adminToggleCategory: (id) => apiRequest(`/admin/blog-categories/${id}/toggle`, { method: 'PATCH' }),
};

export const aboutUsApi = {
  getAboutUs: () => apiRequest('/about-us'),
  adminUpdateAboutUs: (body) => apiRequest('/admin/about-us', { method: 'PUT', body }),
  adminResetAboutUs: () => apiRequest('/admin/about-us/reset', { method: 'POST' }),
};

export const selfHelpApi = {
  getCategories: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/self-help/categories${queryString ? `?${queryString}` : ''}`);
  },
  adminCreateCategory: (body) => apiRequest('/self-help/admin/categories', { method: 'POST', body }),
  adminUpdateCategory: (id, body) => apiRequest(`/self-help/admin/categories/${id}`, { method: 'PUT', body }),
  adminDeleteCategory: (id) => apiRequest(`/self-help/admin/categories/${id}`, { method: 'DELETE' }),
  getPublicResources: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/self-help${queryString ? `?${queryString}` : ''}`);
  },
  getResourceById: (id) => apiRequest(`/self-help/public/${id}`),
  adminGetResources: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/self-help/admin/all${queryString ? `?${queryString}` : ''}`);
  },
  adminCreateResource: (body) => apiRequest('/self-help/admin/create', { method: 'POST', body }),
  adminBulkCreateAudios: (body) => apiRequest('/self-help/admin/bulk-audio', { method: 'POST', body }),
  adminUpdateResource: (id, body) => apiRequest(`/self-help/admin/${id}`, { method: 'PUT', body }),
  adminDeleteResource: (id) => apiRequest(`/self-help/admin/${id}`, { method: 'DELETE' }),
  adminTogglePublish: (id) => apiRequest(`/self-help/admin/${id}/publish`, { method: 'PATCH' }),
  therapistUpload: (body) => apiRequest('/self-help/therapist/upload', { method: 'POST', body }),
};


export const worksheetApi = {
  // Admin Operations
  adminGetWorksheets: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/worksheets${queryString ? `?${queryString}` : ''}`);
  },
  adminGetWorksheetById: (id) => apiRequest(`/admin/worksheets/${id}`),
  adminCreateWorksheet: (body) => apiRequest('/admin/worksheets', { method: 'POST', body }),
  adminUpdateWorksheet: (id, body) => apiRequest(`/admin/worksheets/${id}`, { method: 'PUT', body }),
  adminDeleteWorksheet: (id) => apiRequest(`/admin/worksheets/${id}`, { method: 'DELETE' }),
  adminTogglePublish: (id) => apiRequest(`/admin/worksheets/${id}/toggle`, { method: 'PATCH' }),
  adminGetWorksheetForm: (id) => apiRequest(`/admin/worksheets/${id}/form`),
  adminSaveWorksheetForm: (id, body) => apiRequest(`/admin/worksheets/${id}/form`, { method: 'PUT', body }),
  uploadWorksheetFile: (formData) => fileUploadRequest('/upload/file', formData),

  // Therapist Operations
  therapistGetWorksheets: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/worksheets/therapist${queryString ? `?${queryString}` : ''}`);
  },
  therapistGetWorksheetForm: (id) => apiRequest(`/worksheets/${id}/form`),
  therapistDownloadWorksheet: (id) => apiRequest(`/worksheets/${id}/download`, { method: 'POST' }),
  therapistCreateWorksheet: (body) => apiRequest('/worksheets', { method: 'POST', body }),
};

export const faqApi = {
  // Public Operations
  getPublicFaqs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/faqs/public${queryString ? `?${queryString}` : ''}`);
  },
  getFaqById: (id) => apiRequest(`/faqs/public/${id}`),

  // Admin Operations
  adminGetFaqs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/faqs${queryString ? `?${queryString}` : ''}`);
  },
  adminGetFaqById: (id) => apiRequest(`/admin/faqs/${id}`),
  adminCreateFaq: (body) => apiRequest('/admin/faqs', { method: 'POST', body }),
  adminUpdateFaq: (id, body) => apiRequest(`/admin/faqs/${id}`, { method: 'PUT', body }),
  adminDeleteFaq: (id) => apiRequest(`/admin/faqs/${id}`, { method: 'DELETE' }),
  adminTogglePublish: (id) => apiRequest(`/admin/faqs/${id}/toggle`, { method: 'PATCH' }),
  adminReorderFaqs: (orders) => apiRequest('/admin/faqs/reorder', { method: 'POST', body: { orders } }),
};

export const foundersMessageApi = {
  // Public
  getPublicFoundersMessage: () => apiRequest('/founders-message/public'),

  // Admin
  adminGetFoundersMessages: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/admin/founders-message${queryString ? `?${queryString}` : ''}`);
  },
  adminGetFounderMessageById: (id) => apiRequest(`/admin/founders-message/${id}`),
  adminCreateFounderMessage: (body) => apiRequest('/admin/founders-message', { method: 'POST', body }),
  adminUpdateFounderMessage: (id, body) => apiRequest(`/admin/founders-message/${id}`, { method: 'PUT', body }),
  adminDeleteFounderMessage: (id) => apiRequest(`/admin/founders-message/${id}`, { method: 'DELETE' }),
  adminTogglePublish: (id) => apiRequest(`/admin/founders-message/${id}/toggle`, { method: 'PATCH' }),
  adminReorderFoundersMessages: (orders) => apiRequest('/admin/founders-message/reorder', { method: 'POST', body: { orders } }),
  adminGetSettings: () => apiRequest('/admin/founders-message/settings'),
  adminUpdateSettings: (body) => apiRequest('/admin/founders-message/settings', { method: 'PUT', body }),
  adminResetFoundersData: () => apiRequest('/admin/founders-message/reset', { method: 'POST' }),
  uploadPhoto: (formData) => fileUploadRequest('/upload/file', formData),
};

export const contactApi = {
  // Public
  getPublicSettings: () => apiRequest('/contact/settings'),
  submitEnquiry: (body) => apiRequest('/contact', { method: 'POST', body }),

  // Admin Inquiries / Messages
  adminGetMessages: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/contact/messages${query ? `?${query}` : ''}`);
  },
  adminGetMessageById: (id) => apiRequest(`/admin/contact/messages/${id}`),
  adminUpdateMessage: (id, body) => apiRequest(`/admin/contact/messages/${id}`, { method: 'PUT', body }),
  adminDeleteMessage: (id) => apiRequest(`/admin/contact/messages/${id}`, { method: 'DELETE' }),
  adminBulkDeleteMessages: (ids) => apiRequest('/admin/contact/messages/bulk-delete', { method: 'POST', body: { ids } }),

  // Admin Page Settings
  adminGetSettings: () => apiRequest('/admin/contact/settings'),
  adminUpdateSettings: (body) => apiRequest('/admin/contact/settings', { method: 'PUT', body }),
  adminResetSettings: () => apiRequest('/admin/contact/reset', { method: 'POST' }),
};

// Showcase Slider / Home Section 2 API
export const showcaseApi = {
  // Public
  getPublicShowcase: () => apiRequest('/showcase/public'),

  // Admin Slide Management
  adminGetSlides: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/showcase/slides${query ? `?${query}` : ''}`);
  },
  adminGetSlideById: (id) => apiRequest(`/admin/showcase/slides/${id}`),
  adminCreateSlide: (body) => apiRequest('/admin/showcase/slides', { method: 'POST', body }),
  adminUpdateSlide: (id, body) => apiRequest(`/admin/showcase/slides/${id}`, { method: 'PUT', body }),
  adminDeleteSlide: (id) => apiRequest(`/admin/showcase/slides/${id}`, { method: 'DELETE' }),
  adminToggleStatus: (id) => apiRequest(`/admin/showcase/slides/${id}/toggle`, { method: 'PATCH' }),
  adminReorderSlides: (orders) => apiRequest('/admin/showcase/reorder', { method: 'POST', body: { orders } }),

  // Admin Section Settings
  adminGetSettings: () => apiRequest('/admin/showcase/settings'),
  adminUpdateSettings: (body) => apiRequest('/admin/showcase/settings', { method: 'PUT', body }),
  adminResetData: () => apiRequest('/admin/showcase/reset', { method: 'POST' }),
  uploadImage: (formData) => fileUploadRequest('/upload/file', formData),
};

// Waitlist API
export const waitlistApi = {
  // Public
  joinWaitlist: async (body) => {
    try {
      return await apiRequest('/waitlist', { method: 'POST', body });
    } catch (err) {
      if (err?.message && err.message.toLowerCase().includes('route not found')) {
        try {
          return await apiRequest('/waitlist/join', { method: 'POST', body });
        } catch (subErr) {
          throw err;
        }
      }
      throw err;
    }
  },
  getWaitlistCount: () => apiRequest('/waitlist/count'),

  // Admin
  adminGetWaitlist: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/waitlist${query ? `?${query}` : ''}`);
  },
  adminUpdateWaitlist: (id, body) => apiRequest(`/admin/waitlist/${id}`, { method: 'PATCH', body }),
  adminDeleteWaitlist: (id) => apiRequest(`/admin/waitlist/${id}`, { method: 'DELETE' }),
};


export function getMediaUrl(url) {
  if (!url) return '';
  let target = url;
  if (typeof target === 'object' && target !== null) {
    target = target.url || target.path || target.fileUrl || '';
  }
  if (typeof target !== 'string' || !target.trim()) return '';
  const cleanUrl = target.replace(/\\/g, '/').trim();
  if (
    cleanUrl.startsWith('http://') ||
    cleanUrl.startsWith('https://') ||
    cleanUrl.startsWith('blob:') ||
    cleanUrl.startsWith('data:')
  ) {
    return cleanUrl;
  }

  // If already pointing to frontend public assets
  if (cleanUrl.startsWith('/assets/') || cleanUrl.startsWith('assets/')) {
    return cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
  }

  // Backend uploads / media files
  let backendBase = '';
  if (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL !== '/api') {
    backendBase = process.env.REACT_APP_API_BASE_URL.replace(/\/api\/?$/, '');
  } else if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost' && window.location.port === '3000') {
      backendBase = 'http://localhost:5000';
    } else {
      backendBase = window.location.origin;
    }
  }

  return `${backendBase}${cleanUrl.startsWith('/') ? '' : '/'}${cleanUrl}`;
}

export const badgeApi = {
  getBadges: () => apiRequest('/badges'),
  adminCreateBadge: (body) => apiRequest('/admin/badges/admin', { method: 'POST', body }),
  adminUpdateBadge: (id, body) => apiRequest(`/admin/badges/admin/${id}`, { method: 'PUT', body }),
  adminDeleteBadge: (id) => apiRequest(`/admin/badges/admin/${id}`, { method: 'DELETE' }),
  adminResetBadges: () => apiRequest('/admin/badges/admin/reset', { method: 'POST' }),
  adminUpdateTherapistSessions: (therapistId, completedSessions) =>
    apiRequest(`/admin/badges/admin/therapists/${therapistId}/sessions`, {
      method: 'PATCH',
      body: { completedSessions },
    }),
};

// PsychMitra DigiStore API
export const digiStoreApi = {
  getProducts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/products${query ? `?${query}` : ''}`);
  },
  getProductById: (id) => apiRequest(`/products/${id}`),
  getMyProducts: () => apiRequest('/products/user/myproducts'),
  createProduct: (body) => apiRequest('/products', { method: 'POST', body }),
  updateProduct: (id, body) => apiRequest(`/products/${id}`, { method: 'PATCH', body }),
  deleteProduct: (id) => apiRequest(`/products/${id}`, { method: 'DELETE' }),

  // Admin CRUD
  adminGetProducts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/admin/products${query ? `?${query}` : ''}`);
  },
  adminGetPendingProducts: () => apiRequest('/admin/products/pending'),
  adminGetProductById: (id) => apiRequest(`/admin/products/${id}`),
  adminCreateProduct: (body) => apiRequest('/admin/products', { method: 'POST', body }),
  adminUpdateProduct: (id, body) => apiRequest(`/admin/products/${id}`, { method: 'PUT', body }),
  adminReviewProduct: (id, body) => apiRequest(`/admin/products/${id}`, { method: 'PATCH', body }),
  adminDeleteProduct: (id) => apiRequest(`/admin/products/${id}`, { method: 'DELETE' }),
};

export const productApi = digiStoreApi;

export { fileUploadRequest };


